import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getAllCountries } from '../config/countries';
import { COUNTRY_THEMES } from '../config/countryThemes';

// Floating Shape Component
const FloatingShape = ({ className, delay = 0 }) => (
  <div
    className={`absolute rounded-full opacity-30 blur-3xl ${className}`}
    style={{ animationDelay: `${delay}s` }}
  />
);

// Trust Badge Component
const TrustBadge = ({ icon, text }) => (
  <div className="trust-badge group hover:shadow-md transition-all duration-300">
    <span className="text-primary-500 mr-2 group-hover:scale-110 transition-transform">{icon}</span>
    <span className="text-sm font-medium text-gray-700">{text}</span>
  </div>
);

// Country Card Component — no flag, uses country theme color
const CountryCard = ({ country, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  const countryTheme = COUNTRY_THEMES[country.code];
  const primaryColor = countryTheme?.colors?.primary || '#6366f1';
  const buttonBg = countryTheme?.colors?.buttonBg || `linear-gradient(135deg, ${primaryColor}, ${primaryColor})`;
  const lawRef = countryTheme?.local?.lawRef?.split('·')[0]?.trim() || countryTheme?.legalBadge || '';
  const productName = countryTheme?.local?.productName || 'Rental Agreement';

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="group relative w-full text-left"
    >
      {/* Hover glow */}
      <div
        className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm"
        style={{ background: `linear-gradient(135deg, ${primaryColor}40, ${primaryColor}20)` }}
      />

      {/* Card */}
      <div className={`relative bg-white rounded-2xl shadow-card p-5 transition-all duration-300 border border-gray-100 ${isHovered ? 'shadow-elevated -translate-y-1.5 border-transparent' : ''}`}>

        {/* Colored header band */}
        <div
          className="w-full h-1.5 rounded-full mb-5"
          style={{ background: buttonBg }}
        />

        {/* Country name + law ref */}
        <h4 className="text-lg font-bold text-gray-900 mb-0.5 transition-colors" style={isHovered ? { color: primaryColor } : {}}>
          {country.name}
        </h4>
        <p className="text-xs font-medium mb-3" style={{ color: `${primaryColor}99` }}>
          {lawRef}
        </p>

        {/* Product name */}
        <p className="text-sm text-gray-500 mb-4 line-clamp-1">{productName}</p>

        {/* CTA */}
        <div
          className="flex items-center justify-between px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300"
          style={isHovered
            ? { background: buttonBg, color: 'white', boxShadow: `0 4px 14px ${primaryColor}40` }
            : { backgroundColor: `${primaryColor}12`, color: primaryColor }
          }
        >
          <span>Get Started</span>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </div>
      </div>
    </button>
  );
};

// Feature Card Component
const FeatureCard = ({ icon, iconBg, iconColor, title, description }) => (
  <div className="gradient-border group">
    <div className="bg-white rounded-2xl p-6 h-full transition-all duration-300 group-hover:shadow-lg">
      <div className={`w-14 h-14 ${iconBg} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
        {icon}
      </div>
      <h4 className="text-xl font-semibold text-gray-900 mb-2">{title}</h4>
      <p className="text-gray-600">{description}</p>
    </div>
  </div>
);

const CountrySelector = () => {
  const navigate = useNavigate();
  const countries = getAllCountries();

  const handleCountrySelect = (country) => {
    window.location.href = `${window.location.protocol}//${country.subdomain}.rentydoc.com/`;
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Gradient Background */}
      <div className="fixed inset-0 hero-gradient-bg -z-10" />

      {/* Floating Shapes */}
      <FloatingShape className="w-96 h-96 bg-primary-400 -top-48 -left-48 float-animation" />
      <FloatingShape className="w-80 h-80 bg-accent-400 top-1/4 -right-40 float-animation-delayed" delay={2} />
      <FloatingShape className="w-64 h-64 bg-primary-300 bottom-1/4 left-1/4 float-animation" delay={4} />
      <FloatingShape className="w-72 h-72 bg-accent-300 -bottom-36 right-1/4 float-animation-delayed" delay={1} />

      {/* Particle Overlay */}
      <div className="fixed inset-0 particles-bg -z-5 opacity-50" />

      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2 group">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30 group-hover:shadow-primary-500/50 transition-shadow">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <span className="text-2xl font-bold gradient-text">RentyDoc</span>
            </Link>
            <p className="hidden sm:block text-gray-600 font-medium">Legal Contracts. Globally.</p>
            <div className="flex items-center space-x-3">
              <Link
                to="/login"
                className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium transition-colors"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="btn btn-primary text-sm"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative">
        {/* Hero Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24">
          <div className="text-center max-w-4xl mx-auto animate-fade-in">
            <div className="inline-flex items-center px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-semibold mb-6">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Trusted by 10,000+ landlords worldwide
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
              Professional Rental Contracts
              <br />
              <span className="gradient-text-shine">Around the World</span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto mb-10 leading-relaxed">
              Generate legally compliant rental agreements for your country in minutes.
              <span className="text-primary-600 font-semibold"> No lawyer required.</span>
            </p>

            {/* Trust Badges */}
            <div className="flex flex-wrap items-center justify-center gap-4 mb-12">
              <TrustBadge
                icon={<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>}
                text="Secure & Encrypted"
              />
              <TrustBadge
                icon={<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" /><path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>}
                text="8 Countries Supported"
              />
              <TrustBadge
                icon={<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>}
                text="Instant Download"
              />
            </div>
          </div>

          {/* Country Grid */}
          <div className="animate-slide-up" style={{ animationDelay: '0.2s' }}>
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
              Select Your Country
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {countries.map((country) => (
                <CountryCard
                  key={country.code}
                  country={country}
                  onClick={() => handleCountrySelect(country)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="glass-card p-8 md:p-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Choose <span className="gradient-text">RentyDoc</span>?
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Create professional, legally-binding rental agreements with confidence
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <FeatureCard
                icon={<svg className="w-7 h-7 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>}
                iconBg="bg-primary-100"
                iconColor="text-primary-600"
                title="Legally Compliant"
                description="All contracts comply with local laws and regulations in your jurisdiction"
              />

              <FeatureCard
                icon={<svg className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>}
                iconBg="bg-emerald-100"
                iconColor="text-emerald-600"
                title="Fast & Easy"
                description="Answer simple questions and get your contract in minutes, not days"
              />

              <FeatureCard
                icon={<svg className="w-7 h-7 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
                iconBg="bg-purple-100"
                iconColor="text-purple-600"
                title="Affordable"
                description="One-time payment with no subscriptions or hidden fees"
              />
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-gray-600">Three simple steps to your perfect contract</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {/* Connection Line */}
            <div className="hidden md:block absolute top-16 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-primary-200 via-primary-400 to-primary-200" />

            {/* Step 1 */}
            <div className="relative text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg shadow-primary-500/30 relative z-10">
                1
              </div>
              <div className="glass-card p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Select Your Country</h3>
                <p className="text-gray-600">Choose your location to ensure compliance with local rental laws</p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg shadow-primary-500/30 relative z-10">
                2
              </div>
              <div className="glass-card p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Answer Questions</h3>
                <p className="text-gray-600">Fill in property details, tenant info, and lease terms</p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg shadow-primary-500/30 relative z-10">
                3
              </div>
              <div className="glass-card p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Download & Sign</h3>
                <p className="text-gray-600">Get your professional PDF contract instantly</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-primary-600 via-accent-600 to-primary-700 p-12 md:p-16 text-center shine-effect">
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Ready to Create Your Contract?
              </h2>
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                Join thousands of landlords who trust RentyDoc for their rental agreements
              </p>
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="px-8 py-4 bg-white text-primary-700 font-semibold rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                Get Started Now
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-accent-500 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <span className="text-2xl font-bold">RentyDoc</span>
              </div>
              <p className="text-gray-400 max-w-sm">
                Professional rental contracts for landlords worldwide. Legally compliant, instant delivery.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <div className="space-y-3">
                <Link to="/about" className="block text-gray-400 hover:text-white transition-colors">About Us</Link>
                <Link to="/contact" className="block text-gray-400 hover:text-white transition-colors">Contact</Link>
                <Link to="/login" className="block text-gray-400 hover:text-white transition-colors">Sign In</Link>
              </div>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <div className="space-y-3">
                <Link to="/privacy" className="block text-gray-400 hover:text-white transition-colors">Privacy Policy</Link>
                <Link to="/terms" className="block text-gray-400 hover:text-white transition-colors">Terms of Service</Link>
              </div>
            </div>
          </div>

          {/* Bottom */}
          <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">
              &copy; {new Date().getFullYear()} RentyDoc. All rights reserved.
            </p>
            <div className="flex items-center space-x-6">
              <span className="text-gray-500 text-sm flex items-center">
                <svg className="w-4 h-4 mr-2 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                256-bit SSL Encryption
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CountrySelector;
